import React, { useState, useEffect } from "react";
import WrapperComponent from "../commonComponents/WrapperComponent";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { url } from "../../util/config";
import { useNavigate } from 'react-router-dom';

const ManageRolesComponent = () => {
    const navigate = useNavigate();
    const [moduleMasterState, setModuleMasterState] = useState([]);
    const [roleName, setRoleName] = useState("");
    const [roleNameError, setRoleNameError] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);

    useEffect(() => {
        const fetchModuleMaster = async () => {
            const token = localStorage.getItem('token');
            try {
                const response = await fetch(`${url}/modulemaster`, {
                    method: 'GET',
                    headers: { Authorization: 'Bearer ' + token },
                });
                if (!response.ok) throw new Error("Failed to fetch module master data");
                const data = await response.json();
                setModuleMasterState(data.modules || []);
            } catch (error) {
                toast.error("Failed to load module data. Please refresh.");
            } finally {
                setIsFetching(false);
            }
        };
        fetchModuleMaster();
    }, []);

    const handleToggle = (moduleId, subModuleId = null) => {
        setModuleMasterState((prevState) =>
            prevState.map((module) => {
                if (module.id === moduleId) {
                    if (subModuleId) {
                        const updatedSubModules = module.subModules.map((subModule) =>
                            subModule.id === subModuleId
                                ? { ...subModule, isActive: !subModule.isActive }
                                : subModule
                        );
                        const anySubModuleActive = updatedSubModules.some((subModule) => subModule.isActive);
                        return { ...module, subModules: updatedSubModules, isActive: anySubModuleActive };
                    } else {
                        const newIsActive = !module.isActive;
                        return {
                            ...module,
                            isActive: newIsActive,
                            subModules: module.subModules.map((subModule) => ({ ...subModule, isActive: newIsActive })),
                        };
                    }
                }
                return module;
            })
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!roleName.trim() || roleName.length < 5) {
            setRoleNameError("Role name is required and must be at least 5 characters.");
            return;
        }

        const selectedModules = moduleMasterState
            .filter((module) => module.isActive || module.name === "Logout")
            .map((module) => ({
                id: module.id,
                subModules: module.subModules
                    ? module.subModules.filter((sub) => sub.isActive).map((sub) => ({ id: sub.id }))
                    : [],
            }));

        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/role`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
                body: JSON.stringify({ roleName, selectedModules }),
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                toast.error(err.error || 'Failed to create role. Please try again.', { autoClose: 3000 });
                return;
            }
            toast.success('Role created successfully!', { autoClose: 3000 });
            setTimeout(() => navigate('/userrole'), 3000);
        } catch (error) {
            toast.error('An error occurred. Please try again.', { autoClose: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const activeCount = moduleMasterState.filter(m => m.isActive).length;

    return (
        <WrapperComponent>
            <div style={styles.page}>
                <div style={styles.pageHeader}>
                    <div>
                        <h5 style={styles.pageTitle}>Create Role</h5>
                        <p style={styles.pageSubtitle}>Define a new role and assign module permissions</p>
                    </div>
                    <button style={styles.btnSecondary} onClick={() => navigate('/userrole')}>
                        <i className="bi bi-arrow-left" style={{ marginRight: 6 }}></i>Back to Roles
                    </button>
                </div>

                <form onSubmit={handleSubmit} noValidate>
                    <div style={styles.layout}>
                        {/* Left — Role name + Modules */}
                        <div style={styles.leftCol}>
                            {/* Role Name Card */}
                            <div style={styles.card}>
                                <h6 style={styles.cardTitle}>
                                    <i className="bi bi-shield-lock" style={{ marginRight: 8, color: '#1565c0' }}></i>
                                    Role Details
                                </h6>
                                <div style={styles.fieldGroup}>
                                    <label style={styles.label}>Role Name</label>
                                    <input
                                        type="text"
                                        value={roleName}
                                        placeholder="Enter role name (min 5 characters)"
                                        style={{ ...styles.input, ...(roleNameError ? styles.inputError : {}) }}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^[a-zA-Z0-9 ]*$/.test(value)) {
                                                setRoleName(value);
                                                setRoleNameError('');
                                            }
                                        }}
                                        onBlur={() => {
                                            if (roleName.length < 5) setRoleNameError("Role name must be at least 5 characters.");
                                            else setRoleNameError('');
                                        }}
                                    />
                                    {roleNameError && <span style={styles.fieldError}>{roleNameError}</span>}
                                </div>
                            </div>

                            {/* Modules Card */}
                            <div style={{ ...styles.card, marginTop: 16 }}>
                                <div style={styles.modulesHeader}>
                                    <h6 style={styles.cardTitle}>
                                        <i className="bi bi-grid-3x3-gap" style={{ marginRight: 8, color: '#1565c0' }}></i>
                                        Module Permissions
                                    </h6>
                                    <span style={styles.activeCount}>
                                        {activeCount} of {moduleMasterState.length} selected
                                    </span>
                                </div>

                                {isFetching ? (
                                    <div style={styles.fetchingState}>
                                        <div className="spinner-border text-primary" style={{ width: '1.5rem', height: '1.5rem' }}></div>
                                        <span style={{ fontSize: 13, color: '#6b7280' }}>Loading modules…</span>
                                    </div>
                                ) : (
                                    <div style={styles.moduleList}>
                                        {moduleMasterState.map((module) => (
                                            <div key={module.id} style={styles.moduleCard}>
                                                {/* Module toggle row */}
                                                <div style={styles.moduleRow}>
                                                    <div style={styles.moduleLeft}>
                                                        <div style={styles.moduleIconWrap}>
                                                            <i className={`bi ${module.icon || 'bi-grid'}`} style={{ fontSize: 13, color: '#1565c0' }}></i>
                                                        </div>
                                                        <span style={styles.moduleName}>{module.name}</span>
                                                        {module.name === 'Logout' && (
                                                            <span style={styles.requiredBadge}>Required</span>
                                                        )}
                                                    </div>
                                                    <label style={styles.toggleWrapper}>
                                                        <input
                                                            type="checkbox"
                                                            checked={module.isActive || module.name === 'Logout'}
                                                            disabled={module.name === 'Logout'}
                                                            onChange={() => handleToggle(module.id)}
                                                            style={{ display: 'none' }}
                                                        />
                                                        <span style={{
                                                            ...styles.toggle,
                                                            background: (module.isActive || module.name === 'Logout') ? '#1565c0' : '#d1d5db'
                                                        }}>
                                                            <span style={{
                                                                ...styles.toggleKnob,
                                                                transform: (module.isActive || module.name === 'Logout') ? 'translateX(18px)' : 'translateX(2px)'
                                                            }}></span>
                                                        </span>
                                                    </label>
                                                </div>

                                                {/* SubModules */}
                                                {module.isActive && module.subModules?.length > 0 && (
                                                    <div style={styles.subModuleGrid}>
                                                        {module.subModules.map((sub) => (
                                                            <label key={sub.id} style={{
                                                                ...styles.subModuleChip,
                                                                ...(sub.isActive ? styles.subModuleChipActive : {})
                                                            }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={sub.isActive}
                                                                    onChange={() => handleToggle(module.id, sub.id)}
                                                                    style={{ display: 'none' }}
                                                                />
                                                                <i className={`bi ${sub.isActive ? 'bi-check-circle-fill' : 'bi-circle'}`}
                                                                    style={{ fontSize: 12, marginRight: 5 }}></i>
                                                                {sub.name}
                                                            </label>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right — Summary */}
                        <div style={styles.rightCol}>
                            <div style={styles.summaryCard}>
                                <h6 style={styles.cardTitle}>
                                    <i className="bi bi-list-check" style={{ marginRight: 8, color: '#1565c0' }}></i>
                                    Summary
                                </h6>
                                {roleName && (
                                    <div style={styles.summaryRow}>
                                        <span style={styles.summaryLabel}>Role Name</span>
                                        <span style={styles.summaryValue}>{roleName}</span>
                                    </div>
                                )}
                                <div style={styles.summaryRow}>
                                    <span style={styles.summaryLabel}>Modules</span>
                                    <span style={styles.summaryValue}>{activeCount} selected</span>
                                </div>
                                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {moduleMasterState.filter(m => m.isActive || m.name === 'Logout').map(m => (
                                        <div key={m.id} style={styles.summaryModule}>
                                            <i className="bi bi-check-circle-fill" style={{ color: '#16a34a', fontSize: 12, marginRight: 6 }}></i>
                                            <span style={{ fontSize: 13, color: '#374151' }}>{m.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <button type="submit" style={{ ...styles.btnPrimary, width: '100%', marginTop: 16, justifyContent: 'center', padding: '12px' }} disabled={isLoading}>
                                {isLoading
                                    ? <><span style={styles.spinner}></span> Creating…</>
                                    : <><i className="bi bi-shield-check" style={{ marginRight: 8 }}></i>Save Role</>}
                            </button>
                        </div>
                    </div>
                </form>

                <ToastContainer />
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </WrapperComponent>
    );
};

const styles = {
    page: { display: 'flex', flexDirection: 'column', gap: '16px', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 },
    pageTitle: { margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' },
    pageSubtitle: { margin: '2px 0 0', fontSize: '13px', color: '#6b7280' },
    layout: { display: 'flex', gap: '16px', alignItems: 'flex-start' },
    leftCol: { flex: 2 },
    rightCol: { flex: 1, position: 'sticky', top: 16 },
    card: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    summaryCard: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    cardTitle: { margin: '0 0 16px', fontSize: '14px', fontWeight: '700', color: '#111827', display: 'flex', alignItems: 'center' },
    modulesHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    activeCount: { fontSize: '12px', color: '#6b7280', background: '#f3f4f6', padding: '3px 10px', borderRadius: '20px' },
    fetchingState: { display: 'flex', alignItems: 'center', gap: 10, padding: '20px 0' },
    moduleList: { display: 'flex', flexDirection: 'column', gap: 8 },
    moduleCard: { border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px 14px', background: '#fafafa' },
    moduleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    moduleLeft: { display: 'flex', alignItems: 'center', gap: 10 },
    moduleIconWrap: { width: '28px', height: '28px', borderRadius: '6px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    moduleName: { fontSize: '14px', fontWeight: '600', color: '#111827' },
    requiredBadge: { fontSize: '10px', background: '#fef3c7', color: '#d97706', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' },
    toggleWrapper: { cursor: 'pointer', display: 'flex', alignItems: 'center' },
    toggle: { width: '38px', height: '22px', borderRadius: '11px', display: 'inline-block', position: 'relative', transition: 'background 0.2s', flexShrink: 0 },
    toggleKnob: { position: 'absolute', top: '3px', width: '16px', height: '16px', background: 'white', borderRadius: '50%', transition: 'transform 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' },
    subModuleGrid: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e5e7eb' },
    subModuleChip: { display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', border: '1px solid #e5e7eb', background: 'white', color: '#6b7280', userSelect: 'none' },
    subModuleChipActive: { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' },
    fieldGroup: { display: 'flex', flexDirection: 'column', gap: 5 },
    label: { fontSize: '13px', fontWeight: '600', color: '#374151' },
    input: { padding: '10px 12px', fontSize: '14px', border: '1.5px solid #d1d5db', borderRadius: '8px', outline: 'none', width: '100%', boxSizing: 'border-box', color: '#111827' },
    inputError: { borderColor: '#ef4444' },
    fieldError: { fontSize: '12px', color: '#ef4444' },
    summaryRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f3f4f6' },
    summaryLabel: { fontSize: '12px', color: '#6b7280' },
    summaryValue: { fontSize: '13px', fontWeight: '600', color: '#111827' },
    summaryModule: { display: 'flex', alignItems: 'center' },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    btnSecondary: { display: 'inline-flex', alignItems: 'center', padding: '8px 14px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    spinner: { width: '15px', height: '15px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' },
};

export default ManageRolesComponent;
